import { ListItem, ListItemText, Stack, Typography, FormHelperText } from "@mui/material";
import { prettyPrintDocumentType } from "encamp-shared/src/utils/prettyPrintDocumentType";
import { DocumentType, FacilityDocumentsQuery } from "generated-graphql/graphql";
import { useCallback, useMemo } from "react";
import { FACILITY_DOCUMENTS } from "routes/Customer/Facility/Documents";

import { OmnisearchAutocomplete } from "./OmnisearchAutocomplete";

// disabledDocumentIds is used to disable documents that are already selected
// and should not be selected again
type FacilityDocumentPickerProps = {
  facilityId: string;
  documentTypes: DocumentType[];
  onSelect: (document: FacilityDocumentPickerItem) => void;
  disabled: boolean;
  disabledDocumentIds?: string[];
  selectedDocumentType?: DocumentType;
  acceptableFileFormats?: string[];
  hasInvalidFileExtension?: boolean;
};

export type FacilityDocumentPickerItem = FacilityDocumentsQuery["documents"]["items"][number];

export const FacilityDocumentPicker = ({
  facilityId,
  documentTypes,
  onSelect,
  disabled,
  disabledDocumentIds,
  selectedDocumentType,
  acceptableFileFormats,
  hasInvalidFileExtension,
}: FacilityDocumentPickerProps) => {
  const docHelperText = useMemo(() => {
    if (selectedDocumentType && acceptableFileFormats && acceptableFileFormats.length > 0) {
      if (acceptableFileFormats.length === 1) {
        return `${prettyPrintDocumentType(selectedDocumentType)} must be ${acceptableFileFormats[0]}`;
      }

      return `${prettyPrintDocumentType(selectedDocumentType)} must be ${acceptableFileFormats
        .slice(0, -1)
        .join(", ")} or ${acceptableFileFormats.slice(-1)}`;
    }
    return "";
  }, [selectedDocumentType, acceptableFileFormats]);

  const getDocumentList = useCallback(
    (data: FacilityDocumentsQuery) => {
      if (selectedDocumentType) {
        return data.documents.items.filter((item) => item.documentType === selectedDocumentType);
      }
      return data.documents.items;
    },
    [selectedDocumentType]
  );

  return (
    <Stack spacing={1}>
      <Typography variant="body2">Select an existing document or upload a new file.</Typography>
      <OmnisearchAutocomplete
        runInitialSearch
        disabled={disabled}
        dataQuery={FACILITY_DOCUMENTS}
        defaultSearch={`facilityId:${facilityId} ${
          selectedDocumentType
            ? `documentType:${selectedDocumentType}`
            : documentTypes.length > 0
              ? `documentType:${documentTypes.join("|")}`
              : ""
        }`}
        dataQueryVariables={{
          facilityId,
          sort: [{ field: "createdAt", sort: "desc" }],
          page: 0,
          pageSize: 10,
        }}
        getItems={getDocumentList}
        onSelectionChange={(selected) => {
          if (selected) {
            onSelect(selected);
          }
        }}
        sx={{
          "& .MuiOutlinedInput-root": {
            "& fieldset": {
              borderColor: hasInvalidFileExtension ? "error.main" : undefined,
            },
            "&:hover fieldset": {
              borderColor: hasInvalidFileExtension ? "error.main" : undefined,
            },
            "&.Mui-focused fieldset": {
              borderColor: hasInvalidFileExtension ? "error.main" : undefined,
            },
          },
        }}
        renderOption={(props, item) => (
          <ListItem
            key={item.id}
            {...props}
            disablePadding
            sx={{
              opacity: disabledDocumentIds?.includes(item.id) ? 0.5 : 1,
              pointerEvents: disabledDocumentIds?.includes(item.id) ? "none" : "auto",
            }}
          >
            <ListItemText
              primary={item.title}
              secondary={prettyPrintDocumentType(item.documentType)}
            />
          </ListItem>
        )}
      />
      {docHelperText && (
        <FormHelperText
          sx={{
            pl: 1,
            color: hasInvalidFileExtension ? "error.main" : undefined,
          }}
        >
          {docHelperText}
        </FormHelperText>
      )}
    </Stack>
  );
};
